import { hasInventoryCountPermission, hasPurchasingStockPermission } from "../../utils/purchasingStockPermissions";
/**
 * COMPONENT: StockManagement.jsx
 * Modulo Purchasing - Gestion de inventario y almacen
 * Pilcore PMS (c) Alberto Castro Ull - Markulia Group, S.L.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import { inventoryPresentation, inventoryText } from "./inventoryIntelligenceMessages";
import "../PurchasingUi.css";
import "./InventoryIntelligence.css";
import "../../Reservations.css";
import useIsMobile from "../../hooks/useIsMobile";
import StockOperationsWorkspace from "../components/tabs/StockOperationsWorkspace";
import StockReturnsWorkspace from "../components/tabs/StockReturnsWorkspace";
import StockTransfersWorkspace from "../components/tabs/StockTransfersWorkspace";
import InventoryCountTab from "../components/tabs/InventoryCountTab";
import InventorySnapshotReportTab from "../components/InventorySnapshotReportTab";
import DirectConsumptionPurchases from "../components/DirectConsumptionPurchases";
import { isDirectConsumptionItem } from "../components/itemsFamiliesUi";
import ItemMovementHistory from "../components/ItemMovementHistory";
import { inventoryBasis, historicalExact, quantityFromExact, formatExactQuantity } from "../utils/inventoryUnitQuantity";
import InventoryQuantity from "../components/InventoryQuantity";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import usePurchasingReadAccess from "../components/usePurchasingReadAccess";
import useStockDisplayUnits, { stockUnitIdentity } from "../hooks/useStockDisplayUnits";
import { buildDeliveryNoteUnitOptions } from "../utils/deliveryNoteUnits";
import { getUnitName } from "../utils/unitNames";

const TAB_TITLES = {
  currentStock: "Inventory Intelligence",
  stockRequisitions: "Stock Requisitions",
  stockReturns: "Stock Returns",
  stockTransfers: "Stock Transfers",
  stockMovements: "Stock Dispatched",
  inventoryCount: "Inventory Count",
  snapshotReport: "Snapshot Report",
};

const STOCK_FILTERS = [
  { id: "all", label: "All" },
  { id: "inStock", label: "In Stock" },
  { id: "outOfStock", label: "Out of Stock" },
  { id: "stale", label: "Stale" },
];





const QUANTITY_EPSILON = 0.00000001;
const hasStockQuantity = (value) => Number.isFinite(Number(value)) && Math.abs(Number(value)) >= QUANTITY_EPSILON;
const stockFractionDigits = (quantity) => quantity !== 0 && Math.abs(quantity) < 0.01 ? 8 : 2;

const STOCK_MANAGEMENT_STYLES = `
  .sm-shell {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: 100%;
    min-width: 0;
    padding: 24px;
    color: rgba(237, 243, 255, 0.95);
    background:
      radial-gradient(560px 260px at 8% 0%, rgba(34, 211, 238, 0.12), transparent 72%),
      radial-gradient(560px 260px at 94% 2%, rgba(52, 211, 153, 0.11), transparent 70%),
      linear-gradient(180deg, #07101f 0%, #0a1221 40%, #0b1422 100%);
  }

  .sm-shell--currentStock {
    padding: 0;
    background: transparent;
  }

  .sm-page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
  }

  .sm-hero,
  .sm-card {
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background:
      linear-gradient(180deg, rgba(12, 18, 34, 0.96), rgba(7, 13, 25, 0.96)) padding-box,
      linear-gradient(135deg, rgba(34, 211, 238, 0.18), rgba(59, 130, 246, 0.08), rgba(52, 211, 153, 0.08)) border-box;
    box-shadow: 0 24px 72px rgba(2, 8, 20, 0.3);
    overflow: hidden;
  }

  .sm-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 18px;
    padding: 22px 22px 18px;
  }

  .sm-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    color: rgba(135, 230, 255, 0.84);
    font-size: 0.74rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.18em;
  }

  .sm-title {
    margin: 0;
    font-size: clamp(1.45rem, 2.3vw, 2.2rem);
    font-weight: 900;
    letter-spacing: -0.03em;
    color: rgba(244, 248, 255, 0.98);
  }

  .sm-subtitle {
    margin: 10px 0 0;
    max-width: 720px;
    color: rgba(205, 219, 242, 0.7);
    font-size: 0.92rem;
    line-height: 1.5;
  }

  .sm-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(231, 239, 252, 0.9);
    font-size: 0.8rem;
    font-weight: 800;
  }

  .sm-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .sm-tab {
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(217, 229, 247, 0.8);
    padding: 8px 14px;
    font-size: 0.79rem;
    font-weight: 800;
    cursor: pointer;
    transition: transform 120ms ease, border-color 120ms ease, background 120ms ease;
  }

  .sm-tab:hover {
    transform: translateY(-1px);
  }

  .sm-tab--active {
    border-color: rgba(34, 211, 238, 0.58);
    background: rgba(34, 211, 238, 0.16);
    color: rgba(214, 251, 255, 0.95);
    box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.09);
  }

  .sm-cardHeadMain {
    flex: 1 1 auto;
    min-width: 0;
  }

  .sm-viewTabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .sm-viewTab {
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(217, 229, 247, 0.8);
    padding: 8px 14px;
    font-size: 0.78rem;
    font-weight: 800;
    cursor: pointer;
    transition: transform 120ms ease, border-color 120ms ease, background 120ms ease;
  }

  .sm-viewTab:hover {
    transform: translateY(-1px);
  }

  .sm-viewTab--active {
    border-color: rgba(34, 211, 238, 0.58);
    background: rgba(34, 211, 238, 0.16);
    color: rgba(214, 251, 255, 0.95);
    box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.09);
  }

  .sm-cardHead {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 20px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .sm-cardTitle {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 800;
    color: rgba(241, 246, 255, 0.96);
  }

  .sm-cardSubtitle {
    margin: 6px 0 0;
    color: rgba(190, 205, 227, 0.66);
    font-size: 0.84rem;
  }

  .sm-card--currentStock {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  .sm-cardHead--currentStock {
    flex: 0 0 auto;
    padding-bottom: 12px;
  }

  .sm-controls {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    width: min(560px, 100%);
  }

  .sm-search {
    width: min(420px, 100%);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(240, 245, 255, 0.92);
    padding: 10px 12px;
    outline: none;
  }

  .sm-search::placeholder {
    color: rgba(240, 245, 255, 0.46);
  }

  .sm-search:focus {
    border-color: rgba(48, 216, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(48, 216, 255, 0.15);
  }

  .sm-filterRow {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  .sm-filterRow--mode {
    padding: 3px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.025);
  }

  .sm-filter {
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 0.75rem;
    font-weight: 700;
    color: rgba(226, 234, 247, 0.82);
    background: rgba(255, 255, 255, 0.03);
    cursor: pointer;
  }

  .sm-filter--active {
    border-color: rgba(48, 216, 255, 0.62);
    background: rgba(48, 216, 255, 0.15);
    color: rgba(198, 244, 255, 0.95);
  }

  .sm-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    padding: 14px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
  }

  .sm-stat {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 14px;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.025);
    min-width: 0;
  }

  .sm-statLabel {
    color: rgba(218, 229, 247, 0.63);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.55px;
    font-weight: 700;
    margin-bottom: 4px;
  }

  .sm-statValue {
    color: rgba(238, 244, 255, 0.95);
    font-size: 1.04rem;
    font-weight: 800;
    line-height: 1.1;
  }

  .sm-tableWrap {
    flex: 1 1 auto;
    overflow: auto;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
  }

  .sm-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 1560px;
  }

  .sm-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    text-align: left;
    padding: 12px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.45px;
    font-weight: 800;
    color: rgba(214, 227, 247, 0.8);
    background: linear-gradient(180deg, rgba(25, 31, 53, 0.95), rgba(14, 18, 34, 0.95));
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    white-space: nowrap;
  }

  .sm-table tbody tr {
    background: rgba(255, 255, 255, 0.018);
    transition: background 120ms ease, box-shadow 120ms ease, transform 120ms ease;
  }

  .sm-table tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.012);
  }

  .sm-table tbody tr:hover {
    background: rgba(48, 216, 255, 0.055);
    transform: translateY(-1px);
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.22);
  }

  .sm-table tbody td {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    color: rgba(234, 241, 252, 0.87);
    font-size: 0.84rem;
    vertical-align: middle;
    white-space: nowrap;
  }

  .sm-code {
    color: rgba(126, 229, 255, 0.94);
    font-size: 0.74rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .sm-itemName {
    margin-top: 4px;
    font-size: 0.9rem;
    font-weight: 800;
    color: rgba(244, 248, 255, 0.96);
  }

  .sm-hierarchy {
    color: rgba(210, 222, 242, 0.66);
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .sm-cellStrong {
    color: rgba(244, 248, 255, 0.96);
    font-weight: 800;
  }

  .sm-cellMuted {
    color: rgba(210, 222, 242, 0.74);
  }

  .sm-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 94px;
    padding: 5px 10px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 0.73rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .sm-pill--in {
    background: rgba(46, 204, 113, 0.13);
    color: rgba(188, 248, 214, 0.96);
    border-color: rgba(46, 204, 113, 0.44);
  }

  .sm-pill--out {
    background: rgba(231, 76, 60, 0.13);
    color: rgba(255, 207, 201, 0.96);
    border-color: rgba(231, 76, 60, 0.44);
  }

  .sm-pill--stale {
    background: rgba(255, 193, 7, 0.14);
    color: rgba(255, 228, 143, 0.98);
    border-color: rgba(255, 193, 7, 0.45);
  }

  .sm-num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .sm-quantityValue {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    white-space: nowrap;
  }

  .sm-quantityUnit,
  .sm-unitBadge {
    display: inline-flex;
    align-items: center;
    max-width: 150px;
    padding: 2px 7px;
    border: 1px solid rgba(34, 211, 238, 0.28);
    border-radius: 999px;
    background: rgba(34, 211, 238, 0.09);
    color: rgba(180, 241, 255, 0.94);
    font-size: 0.66rem;
    font-weight: 850;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sm-unitBadge {
    padding: 4px 8px;
    font-size: 0.7rem;
  }

  .sm-table--locations th,
  .sm-table--locations td {
    vertical-align: middle;
  }

  .sm-locationQty {
    color: rgba(238, 244, 255, 0.92);
    font-weight: 800;
  }

  .sm-locationQty--empty {
    color: rgba(210, 222, 242, 0.38);
    font-weight: 700;
  }

  .sm-locationMovementBtn {
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: flex-end;
    border: 0;
    padding: 3px 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
  }

  .sm-locationMovementBtn:hover,
  .sm-locationMovementBtn:focus-visible {
    color: rgba(139, 233, 255, 0.98);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .sm-locationTotal {
    color: rgba(184, 244, 255, 0.96);
    font-weight: 900;
  }

  .sm-actionBtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid rgba(34, 211, 238, 0.4);
    background: rgba(34, 211, 238, 0.1);
    color: rgba(214, 251, 255, 0.96);
    padding: 7px 12px;
    font-size: 0.74rem;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 120ms ease, border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
  }

  .sm-actionBtn:hover {
    transform: translateY(-1px);
    border-color: rgba(34, 211, 238, 0.68);
    background: rgba(34, 211, 238, 0.16);
    box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.08);
  }

  .sm-actionBtn:disabled {
    cursor: not-allowed;
    opacity: 0.48;
    transform: none;
    box-shadow: none;
  }

  .sm-mobileList {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    overflow: auto;
    padding: 14px;
  }

  .sm-mobileCard {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.03);
    padding: 12px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.26);
  }

  .sm-mobileTop {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 8px;
  }

  .sm-mobileRows {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 10px;
    margin-top: 10px;
  }

  .sm-mobileLabel {
    font-size: 0.67rem;
    color: rgba(206, 219, 243, 0.58);
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 2px;
    font-weight: 700;
  }

  .sm-mobileValue {
    font-size: 0.82rem;
    color: rgba(232, 239, 251, 0.9);
    overflow-wrap: anywhere;
  }

  .sm-mobileActions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }

  .sm-mobileLocationList {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 10px;
  }

  .sm-mobileLocationRow {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    color: rgba(226, 236, 251, 0.85);
    font-size: 0.8rem;
  }

  .sm-mobileLocationButton {
    width: 100%;
    align-items: center;
    border-top: 0;
    border-right: 0;
    border-left: 0;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .sm-mobileLocationButton:hover,
  .sm-mobileLocationButton:focus-visible {
    color: rgba(139, 233, 255, 0.98);
  }

  .sm-mobileLocationRow:last-child {
    border-bottom: 0;
  }

  .sm-empty {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: rgba(204, 217, 241, 0.65);
    padding: 24px 14px;
    font-size: 0.95rem;
  }

  .sm-cardBody {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }

  .sm-page--currentStock {
    gap: 10px;
  }

  .sm-modalOverlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: rgba(2, 7, 18, 0.78);
    backdrop-filter: blur(14px);
  }

  .sm-ledgerModal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background:
      linear-gradient(180deg, rgba(12, 18, 34, 0.98), rgba(7, 13, 25, 0.98)) padding-box,
      linear-gradient(135deg, rgba(34, 211, 238, 0.22), rgba(59, 130, 246, 0.1), rgba(52, 211, 153, 0.12)) border-box;
    box-shadow: 0 30px 90px rgba(0, 0, 0, 0.46);
    overflow: hidden;
    color: rgba(237, 243, 255, 0.95);
  }

  .sm-ledgerHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 18px;
    padding: 20px 22px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background:
      radial-gradient(720px 220px at 8% 0%, rgba(34, 211, 238, 0.08), transparent 72%),
      radial-gradient(720px 220px at 94% 0%, rgba(52, 211, 153, 0.08), transparent 72%),
      rgba(255, 255, 255, 0.018);
  }

  .sm-ledgerKicker {
    color: rgba(135, 230, 255, 0.84);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .sm-ledgerTitle {
    margin: 6px 0 0;
    color: rgba(244, 248, 255, 0.98);
    font-size: 1.24rem;
    font-weight: 900;
    line-height: 1.15;
  }

  .sm-ledgerMeta {
    margin-top: 8px;
    color: rgba(205, 219, 242, 0.68);
    font-size: 0.82rem;
    line-height: 1.45;
  }

  .sm-ledgerHeaderActions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .sm-ledgerClose {
    min-width: 38px;
    padding: 7px 10px;
  }

  .sm-ledgerBody {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px 16px;
    overflow: hidden;
    min-height: 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
  }

  .sm-ledgerStats {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
    gap: 8px;
  }

  .sm-ledgerScopeBar {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.025);
  }

  .sm-ledgerScopeLabel {
    margin-right: 2px;
    color: rgba(205, 219, 242, 0.68);
    font-size: 0.72rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .sm-ledgerScopeBtn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    padding: 7px 11px;
    background: rgba(255, 255, 255, 0.04);
    color: rgba(217, 229, 247, 0.82);
    font-size: 0.72rem;
    font-weight: 800;
    cursor: pointer;
  }

  .sm-ledgerScopeBtn--active {
    border-color: rgba(34, 211, 238, 0.58);
    background: rgba(34, 211, 238, 0.16);
    color: rgba(214, 251, 255, 0.98);
  }

  .sm-ledgerScopeQty {
    margin-left: 5px;
    color: rgba(255, 255, 255, 0.62);
    font-variant-numeric: tabular-nums;
  }

  .sm-ledgerContextNotice {
    flex: 0 0 auto;
    padding: 8px 11px;
    border: 1px solid rgba(251, 191, 36, 0.22);
    border-radius: 10px;
    background: rgba(251, 191, 36, 0.07);
    color: rgba(254, 240, 175, 0.88);
    font-size: 0.7rem;
    line-height: 1.4;
  }

  .sm-provenance {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: minmax(190px, 0.7fr) minmax(280px, 1.5fr) minmax(170px, 0.65fr);
    gap: 12px;
    align-items: stretch;
    padding: 12px 14px;
    border: 1px solid rgba(34, 211, 238, 0.2);
    border-radius: 14px;
    background:
      linear-gradient(135deg, rgba(34, 211, 238, 0.06), rgba(59, 130, 246, 0.025)),
      rgba(255, 255, 255, 0.018);
  }

  .sm-provenanceTitle {
    color: rgba(237, 247, 255, 0.96);
    font-size: 0.8rem;
    font-weight: 900;
  }

  .sm-provenanceHint,
  .sm-provenanceFormula,
  .sm-provenanceResult {
    margin-top: 4px;
    color: rgba(205, 219, 242, 0.68);
    font-size: 0.7rem;
    line-height: 1.45;
  }

  .sm-provenanceFormula strong,
  .sm-provenanceResult strong {
    color: rgba(239, 247, 255, 0.94);
    font-variant-numeric: tabular-nums;
  }

  .sm-provenanceEvents {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 7px;
  }

  .sm-provenanceEvent {
    border: 1px solid rgba(125, 211, 252, 0.18);
    border-radius: 7px;
    padding: 3px 6px;
    background: rgba(125, 211, 252, 0.055);
    color: rgba(216, 240, 255, 0.8);
    font-size: 0.65rem;
    line-height: 1.3;
  }

  .sm-provenanceStatus {
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    padding-left: 12px;
  }

  .sm-provenanceStatusLabel {
    color: rgba(167, 243, 208, 0.95);
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .sm-provenanceStatus--warning .sm-provenanceStatusLabel {
    color: rgba(253, 230, 138, 0.96);
  }

  .sm-provenanceStatus--bad .sm-provenanceStatusLabel {
    color: rgba(254, 202, 202, 0.98);
  }

  .sm-locationRole {
    display: inline-flex;
    max-width: 220px;
    color: rgba(216, 228, 247, 0.82);
    font-size: 0.7rem;
    font-weight: 750;
    line-height: 1.35;
    white-space: normal;
  }

  .sm-ledgerSection {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    gap: 10px;
  }

  .sm-ledgerSectionHead {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }

  .sm-ledgerSectionTitle {
    color: rgba(241, 246, 255, 0.96);
    font-size: 0.94rem;
    font-weight: 900;
  }

  .sm-ledgerPageActions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sm-ledgerPageStatus {
    color: rgba(204, 217, 241, 0.64);
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
  }

  .sm-ledgerLoadMore {
    min-height: 32px;
    padding: 6px 10px;
    font-size: 0.72rem;
  }

  .sm-ledgerTableWrap {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: auto;
    overflow-y: auto;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.018);
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
  }

  .sm-table--ledger {
    width: 100%;
    min-width: 0;
    table-layout: fixed;
  }

  .sm-table--ledger thead th,
  .sm-table--ledger tbody td {
    overflow: hidden;
    padding: clamp(6px, 0.58vw, 10px) clamp(5px, 0.5vw, 9px);
    font-size: clamp(0.62rem, 0.68vw, 0.78rem);
    text-overflow: ellipsis;
  }

  .sm-table--ledger thead th {
    line-height: 1.15;
    white-space: normal;
  }

  .sm-table--ledger tbody td {
    white-space: nowrap;
  }

  .sm-table--ledger tbody td.sm-typeCell {
    overflow: visible;
  }

  .sm-table--locationLedger th,
  .sm-table--locationLedger td {
    width: auto !important;
  }

  .sm-table--locationLedger {
    min-width: 1220px;
    table-layout: auto;
  }

  .sm-table--globalLedger {
    min-width: 1380px;
    table-layout: auto;
  }

  .sm-table--globalLedger th,
  .sm-table--globalLedger td {
    width: auto !important;
  }

  .sm-table--ledger th:nth-child(1),
  .sm-table--ledger td:nth-child(1) {
    width: 7%;
  }

  .sm-table--ledger th:nth-child(2),
  .sm-table--ledger td:nth-child(2) {
    width: 9%;
  }

  .sm-table--ledger th:nth-child(3),
  .sm-table--ledger td:nth-child(3) {
    width: 11%;
  }

  .sm-table--ledger th:nth-child(4),
  .sm-table--ledger td:nth-child(4) {
    width: 7%;
  }

  .sm-table--ledger th:nth-child(5),
  .sm-table--ledger td:nth-child(5),
  .sm-table--ledger th:nth-child(6),
  .sm-table--ledger td:nth-child(6),
  .sm-table--ledger th:nth-child(8),
  .sm-table--ledger td:nth-child(8),
  .sm-table--ledger th:nth-child(9),
  .sm-table--ledger td:nth-child(9) {
    width: 9%;
  }

  .sm-table--ledger th:nth-child(7),
  .sm-table--ledger td:nth-child(7) {
    width: 8%;
  }

  .sm-table--ledger th:nth-child(10),
  .sm-table--ledger td:nth-child(10) {
    width: 10%;
  }

  .sm-table--ledger th:nth-child(11),
  .sm-table--ledger td:nth-child(11) {
    width: 12%;
  }

  .sm-moveType {
    min-width: 118px;
    text-transform: none;
    letter-spacing: 0;
  }

  .sm-moveType--positive {
    background: rgba(46, 204, 113, 0.12);
    color: rgba(188, 248, 214, 0.96);
    border-color: rgba(46, 204, 113, 0.38);
  }

  .sm-moveType--negative {
    background: rgba(248, 113, 113, 0.13);
    color: rgba(255, 211, 211, 0.96);
    border-color: rgba(248, 113, 113, 0.42);
  }

  .sm-moveType--neutral {
    background: rgba(148, 163, 184, 0.13);
    color: rgba(226, 232, 240, 0.92);
    border-color: rgba(148, 163, 184, 0.34);
  }

  .sm-table--ledger .sm-moveType {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    padding-left: 6px;
    padding-right: 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sm-moveTypeWrap {
    position: relative;
    display: inline-flex;
    width: 100%;
    padding-bottom: 7px;
  }

  .sm-correctedBadge {
    position: absolute;
    right: -3px;
    bottom: -1px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 17px;
    padding: 2px 7px;
    border: 1px solid rgba(103, 232, 249, 0.58);
    border-radius: 999px;
    background: rgba(8, 20, 38, 0.98);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.34);
    color: rgba(190, 246, 255, 0.98);
    font-size: 0.56rem;
    font-weight: 900;
    letter-spacing: 0.03em;
    line-height: 1;
    text-transform: none;
    white-space: nowrap;
  }

  .sm-accountingDates {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    white-space: nowrap;
  }

  .sm-accountingDateCorrection {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: rgba(190, 246, 255, 0.98);
    font-size: 0.68rem;
    font-weight: 800;
  }

  .sm-accountingDateCorrection::before {
    width: 5px;
    height: 5px;
    border-radius: 999px;
    background: rgba(103, 232, 249, 0.92);
    box-shadow: 0 0 0 3px rgba(103, 232, 249, 0.12);
    content: "";
    flex: 0 0 auto;
  }

  .sm-positive {
    color: rgba(188, 248, 214, 0.96);
  }

  .sm-negative {
    color: rgba(255, 211, 211, 0.96);
  }

  .sm-reference {
    color: rgba(210, 222, 242, 0.7);
    font-size: 0.76rem;
  }

  .sm-referenceBtn {
    display: inline-block;
    max-width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: rgba(125, 211, 252, 0.96);
    font: inherit;
    font-weight: 800;
    text-align: left;
    text-decoration: underline;
    text-decoration-color: rgba(125, 211, 252, 0.42);
    text-underline-offset: 3px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sm-referenceBtn:hover,
  .sm-referenceBtn:focus-visible {
    color: rgba(186, 230, 253, 1);
    text-decoration-color: rgba(186, 230, 253, 0.78);
  }

  .sm-ledgerEmpty,
  .sm-ledgerError {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 150px;
    padding: 20px;
    text-align: center;
    color: rgba(204, 217, 241, 0.68);
  }

  .sm-ledgerError {
    flex-direction: column;
    gap: 12px;
    color: rgba(255, 188, 188, 0.95);
  }

  @media (max-width: 920px) {
    .sm-hero,
    .sm-cardHead {
      flex-direction: column;
      align-items: stretch;
    }

    .sm-controls {
      width: 100%;
      align-items: stretch;
    }

    .sm-filterRow {
      justify-content: flex-start;
    }

    .sm-ledgerHeader {
      flex-direction: column;
      align-items: stretch;
    }

    .sm-ledgerHeaderActions {
      justify-content: flex-end;
    }

    .sm-provenance {
      grid-template-columns: minmax(170px, 0.75fr) minmax(250px, 1.25fr);
    }

    .sm-provenanceStatus {
      grid-column: 1 / -1;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      border-left: 0;
      padding-top: 9px;
      padding-left: 0;
    }
  }

  @media (max-width: 640px) {
    .sm-shell {
      padding: 12px;
    }

    .sm-shell--currentStock {
      padding: 0;
    }

    .sm-hero {
      padding: 16px;
      border-radius: 18px;
    }

    .sm-card {
      border-radius: 18px;
    }

    .sm-title {
      font-size: 1.18rem;
    }

    .sm-subtitle,
    .sm-cardSubtitle {
      font-size: 0.78rem;
    }

    .sm-stats {
      padding: 10px 12px;
      gap: 6px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .sm-stat {
      padding: 8px 9px;
      border-radius: 10px;
    }

    .sm-statValue {
      font-size: 0.86rem;
    }

    .sm-mobileRows {
      gap: 6px 8px;
    }

    .sm-modalOverlay {
      padding: 0;
    }

    .sm-ledgerModal {
      width: 100vw;
      height: 100dvh;
      max-height: 100dvh;
      border-radius: 0;
    }

    .sm-ledgerHeader,
    .sm-ledgerBody {
      padding-left: 14px;
      padding-right: 14px;
    }

    .sm-ledgerBody {
      overflow-y: auto;
    }

    .sm-ledgerScopeBar {
      max-height: 28dvh;
      overflow-y: auto;
    }

    .sm-ledgerSection {
      flex: 0 0 auto;
      min-height: 320px;
    }

    .sm-provenance {
      grid-template-columns: 1fr;
    }

    .sm-provenanceStatus {
      grid-column: auto;
    }
  }

  @media (max-height: 820px) {
    .sm-shell { zoom: 0.9; height: calc((100dvh - 28px) / 0.9); }
  }

  @media (max-height: 720px) {
    .sm-shell { zoom: 0.8; height: calc((100dvh - 28px) / 0.8); }
  }

  @media (max-height: 620px) {
    .sm-shell { zoom: 0.7; height: calc((100dvh - 28px) / 0.7); }
  }
`;

function getStockStatus(quantity, lastMovementAt) {
  if (quantity <= 0) {
    return { key: "out", label: "Out", toneClass: "sm-pill--out" };
  }

  if (!lastMovementAt) {
    return { key: "stale", label: "Stale", toneClass: "sm-pill--stale" };
  }

  const diffDays = (Date.now() - new Date(lastMovementAt).getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays > 30) {
    return { key: "stale", label: "Stale", toneClass: "sm-pill--stale" };
  }

  return { key: "in", label: "In Stock", toneClass: "sm-pill--in" };
}

function sortLocations(locations) {
  return [...locations].sort((left, right) => {
    const leftSort = Number(left?.sort_order || 0);
    const rightSort = Number(right?.sort_order || 0);
    if (leftSort !== rightSort) return leftSort - rightSort;
    if (Boolean(left?.is_main) !== Boolean(right?.is_main)) return left?.is_main ? -1 : 1;
    return String(left?.name || "").localeCompare(String(right?.name || ""));
  });
}

function buildRow(balance, index, languageId) {
  const item = balance.item || {};
  const category = item.category || {};
  const subfamily = category.subfamily || {};
  const family = subfamily.family || {};
  const subcategory = item.subcategory || {};
  const unit = item.unit || {};
  const unitName = getUnitName(unit, languageId) || inventoryText(languageId, "Unit not set");
  const unitOptions = buildDeliveryNoteUnitOptions(item, undefined, languageId).filter((option) =>
    Number.isFinite(option.factor) && option.factor > 0
    && (option.level === "unit" || item[option.level]?.name)
  );

  const basis = inventoryBasis(item);
  const exact = balance.exact_quantity || historicalExact(balance.current_qty, basis);
  const quantity = Number(quantityFromExact(exact, "unit", 24) ?? balance.current_qty ?? 0);
  const totalValue = Number(balance.current_value || 0);
  const locationBalances = Array.isArray(balance.location_balances)
    ? balance.location_balances
    : [];
  const locationQtyById = new Map();
  const locationExactById = new Map();

  locationBalances.forEach((locationBalance) => {
    const locationId = Number(locationBalance.location_id);
    if (!Number.isFinite(locationId)) return;
    if (locationBalance.exact_quantity) locationExactById.set(locationId, locationBalance.exact_quantity);
    locationQtyById.set(locationId, Number(quantityFromExact(locationBalance.exact_quantity, "unit", 24) ?? locationBalance.current_qty ?? 0));
  });

  const locationTotalQty = balance.location_total_qty != null
    ? Number(balance.location_total_qty)
    : Number(Array.from(locationQtyById.values()).reduce((sum, value) => sum + value, 0).toFixed(8));
  const legacyUnallocatedQty =
    balance.location_access_scope === "user_locations"
      ? null
      : Number.isFinite(Number(balance.legacy_unallocated_qty))
        ? Number(balance.legacy_unallocated_qty)
        : Number((quantity - locationTotalQty).toFixed(8));

  return {
    id: `${item.id || item.code || "stock"}-${index}`,
    itemId: item?.id || null,
    familyName: family?.name || "-",
    subfamilyName: subfamily?.name || "-",
    categoryName: category?.name || "-",
    subcategoryName: subcategory?.name || "-",
    itemCode: item?.code || "-",
    itemName: item?.name || "-",
    unitName,
    unitCode: unit.code || "",
    unitOptions: unitOptions.some((option) => option.level === "unit") ? unitOptions : [],
    quantity,
    exactQuantity: exact,
    storedExact: balance.exact_quantity,
    physicalBasis: basis,
    totalValue,
    locationBalances,
    locationQtyById,
    locationExactById,
    locationTotalQty,
    legacyUnallocatedQty,
    locationAccessScope: balance.location_access_scope || "all",
    lastMovementAt: balance.last_movement_at || null,
    status: getStockStatus(quantity, balance.last_movement_at),
    searchBlob: [
      family?.name,
      subfamily?.name,
      category?.name,
      subcategory?.name,
      item?.code,
      item?.name,
      unit?.name,
      unit?.code,
      unitName,
      ...unitOptions.map((option) => option.label),
      ...locationBalances.map((locationBalance) => locationBalance.location?.name),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
}

export default function StockManagement({ forcedTab = null, pendingApprovalsOnly = false }) {
  const { themeId, languageId } = usePmsTheme();
  const { t, formatDate, formatDecimal, formatCurrency } = useMemo(() => inventoryPresentation(languageId, 2), [languageId]);
  const preciseFormat = useMemo(() => inventoryPresentation(languageId, 4).formatDecimal, [languageId]);
  const renderLocationQuantity = (value, row, showUnit = true, exact = null) => {
    const formatted = formatExactQuantity(exact, "unit", languageId);
    if (formatted && formatted !== "0") return showUnit
      ? <InventoryQuantity value={value} formatted={formatted} unitName={row.unitName} /> : formatted;
    const unitName = row.unitName;
    const quantity = Number(Number(value || 0).toFixed(8));
    if (quantity === 0) return <span className="sm-locationQty--empty">-</span>;
    const digits = historicalExact(value, row.physicalBasis) ? 4 : stockFractionDigits(quantity);
    return showUnit || digits === 8
      ? <InventoryQuantity value={quantity} unitName={unitName} maximumFractionDigits={digits} />
      : (digits === 4 ? preciseFormat(quantity) : formatDecimal(quantity));
  };
  const resolvedForcedTab = forcedTab === "snapshotReport" ? "currentStock" : forcedTab;
  const initialInventoryView = forcedTab === "snapshotReport" ? "snapshot" : "current";
  const [, refreshSession] = useState(0);
  useEffect(() => {
    const update = () => refreshSession((version) => version + 1);
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener("palantir-session-updated", update); window.removeEventListener("storage", update); };
  }, []);
  const currentUser = readStoredUser();
  const canViewStockRequisitionFlow =
    hasPurchasingStockPermission(currentUser, "purchasing.stock.requisitions");
  const canViewStockDispatchFlow =
    hasPurchasingStockPermission(currentUser, "purchasing.stock.dispatched");
  const canViewStockTransfers = hasPurchasingStockPermission(currentUser, "purchasing.stock.transfers");

  const businessUnitId = Number(currentUser?.active_business_unit_id || 0) || null;
  const { can, requestConfig, contextKey } = usePurchasingReadAccess(businessUnitId);
  const canViewCurrentStock = businessUnitId ? can("purchasing.stock.current") : hasPurchasingStockPermission(currentUser, "purchasing.stock.current");
  const canViewSnapshot = businessUnitId
    ? can("purchasing.stock.current.snapshot_report") || can("purchasing.stock.current.accounting_closures.view")
    : hasPurchasingStockPermission(currentUser, "purchasing.stock.current.snapshot_report") || hasPurchasingStockPermission(currentUser, "purchasing.stock.current.accounting_closures.view");
  const canViewMovements = can("purchasing.stock.current.movements");
  const tabPermissions = {
    currentStock: canViewCurrentStock || canViewSnapshot,
    stockRequisitions: canViewStockRequisitionFlow || canViewStockDispatchFlow,
    stockReturns: hasPurchasingStockPermission(currentUser, "purchasing.stock.returns"),
    stockTransfers: canViewStockTransfers,
    stockMovements: canViewStockDispatchFlow,
    inventoryCount: hasInventoryCountPermission(currentUser, "view"),
    snapshotReport: canViewSnapshot,
  };

  const defaultTab = tabPermissions.currentStock
    ? "currentStock"
    : tabPermissions.stockTransfers
    ? "stockTransfers"
    : tabPermissions.stockRequisitions
    ? "stockRequisitions"
    : tabPermissions.stockMovements
    ? "stockMovements"
    : tabPermissions.stockReturns
    ? "stockReturns"
    : tabPermissions.inventoryCount
    ? "inventoryCount"
    : null;

  const [activeTab, setActiveTab] = useState(resolvedForcedTab || defaultTab);
  const [inventoryView, setInventoryView] = useState(canViewCurrentStock ? initialInventoryView : "snapshot");
  const [currentStockMode, setCurrentStockMode] = useState("summary");
  const [stock, setStock] = useState([]);
  const [stockLocations, setStockLocations] = useState([]);
  const [loading, setLoading] = useState((resolvedForcedTab || defaultTab) === "currentStock");
  const [loadedScope, setLoadedScope] = useState(null);
  const [stockError, setStockError] = useState(false);
  const [locationsError, setLocationsError] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const stockRequestRef = useRef(0);
  const [stockSearch, setStockSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [movementModalRow, setMovementModalRow] = useState(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();
  const isMobile = useIsMobile();
  const isSidebarMode = Boolean(forcedTab);
  const activeTabAllowed = activeTab ? Boolean(tabPermissions[activeTab]) : false;
  const isCurrentStockTab = activeTab === "currentStock";
  const isStockOperationsTab = activeTab === "stockRequisitions" || activeTab === "stockMovements";
  const isStockTransfersTab = activeTab === "stockTransfers";
  const isStockReturnsTab = activeTab === "stockReturns";
  const isInventoryCountTab = activeTab === "inventoryCount";
  const isStockOperationsWorkspace = isStockOperationsTab || isStockTransfersTab || isStockReturnsTab;
  const isPilcoreStockWorkspace = isCurrentStockTab || isInventoryCountTab || isStockOperationsWorkspace;
  const mainActiveTab = isStockOperationsTab ? "stockRequisitions" : activeTab;
  const showHero = !isCurrentStockTab && !isStockOperationsTab && !isStockTransfersTab && !isStockReturnsTab && !isInventoryCountTab;
  const useCompactWorkspaceShell = isCurrentStockTab || isStockOperationsTab || isStockTransfersTab || isStockReturnsTab || isInventoryCountTab;

  const { choices: stockDisplayUnits, selectUnit: selectStockDisplayUnit } = useStockDisplayUnits(currentUser?.id, businessUnitId);
  const businessUnitRef = useRef(businessUnitId);
  businessUnitRef.current = businessUnitId;

  const closeMovementLedger = useCallback(() => setMovementModalRow(null), []);
  const openMovementLedger = useCallback(
    (row, initialLocationScope = "global", initialLocationSelection = null) => {
      if (!row?.itemId || !can("purchasing.stock.current.movements", true)) return;
      setMovementModalRow({ ...row, initialLocationScope, initialLocationSelection });
    },
    [can]
  );

  const fetchCurrentStock = useCallback(async () => {
    if (!can("purchasing.stock.current", true)) return;
    const request = ++stockRequestRef.current;
    const isCurrent = () => request === stockRequestRef.current && businessUnitRef.current === businessUnitId && can("purchasing.stock.current", true);
    setLoading(true); setStockError(false); setLocationsError(false);
    try {
      const [res, locationsRes] = await Promise.all([
        axios.get("/api/purchasing/inventory/balances", requestConfig("purchasing.stock.current")),
        axios.get("/api/purchasing/inventory/locations", requestConfig("purchasing.stock.current")).catch((error) => {
          if (isCurrent()) setLocationsError(true);
          console.warn("[STOCK][LOCATIONS][WARN] Could not load inventory locations:", error);
          return { data: [] };
        }),
      ]);

      if (!isCurrent()) return;

      const sorted = [...(Array.isArray(res.data) ? res.data : [])].sort((a, b) => {
        const A = a.item || {};
        const B = b.item || {};

        const famA = A.category?.subfamily?.family?.name || "";
        const famB = B.category?.subfamily?.family?.name || "";
        if (famA.localeCompare(famB) !== 0) return famA.localeCompare(famB);

        const subfamA = A.category?.subfamily?.name || "";
        const subfamB = B.category?.subfamily?.name || "";
        if (subfamA.localeCompare(subfamB) !== 0) return subfamA.localeCompare(subfamB);

        const catA = A.category?.name || "";
        const catB = B.category?.name || "";
        if (catA.localeCompare(catB) !== 0) return catA.localeCompare(catB);

        const subcatA = A.subcategory?.name || "";
        const subcatB = B.subcategory?.name || "";
        return subcatA.localeCompare(subcatB);
      });

      setStock(sorted);
      setStockLocations(sortLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []));
    } catch (err) {
      if (!isCurrent()) return;
      setStockError(true);
      console.error("[STOCK][ERROR] Error fetching balances:", err);
    } finally {
      if (isCurrent()) { setLoading(false); setLoadedScope(businessUnitId); }
    }
  }, [businessUnitId, can, requestConfig]);

  useEffect(() => {
    stockRequestRef.current += 1;
    setStock([]); setStockLocations([]); setLoadedScope(null); setStockError(false); setLocationsError(false);
    closeMovementLedger();
    return () => { stockRequestRef.current += 1; };
  }, [businessUnitId, contextKey, closeMovementLedger]);

  useEffect(() => {
    if (!forcedTab) return;
    setActiveTab(resolvedForcedTab);
    setInventoryView(canViewCurrentStock ? initialInventoryView : "snapshot");
  }, [forcedTab, initialInventoryView, resolvedForcedTab, canViewCurrentStock]);

  useEffect(() => {
    if (inventoryView === "snapshot" && !canViewSnapshot && canViewCurrentStock) setInventoryView("current");
    if (inventoryView !== "snapshot" && !canViewCurrentStock && canViewSnapshot) setInventoryView("snapshot");
  }, [inventoryView, canViewCurrentStock, canViewSnapshot]);

  useEffect(() => {
    if (resolvedForcedTab) return;
    if (activeTabAllowed) return;
    setActiveTab(defaultTab);
  }, [activeTabAllowed, defaultTab, resolvedForcedTab]);

  useEffect(() => {
    if (activeTab !== "currentStock") {
      setLoading(false);
      return;
    }
    if (!businessUnitId || !activeTabAllowed || inventoryView !== "current") return;
    fetchCurrentStock();
    const refreshAfterDeliveryNote = () => { fetchCurrentStock(); };
    window.addEventListener("refreshDeliveryNotesList", refreshAfterDeliveryNote);
    return () => window.removeEventListener("refreshDeliveryNotesList", refreshAfterDeliveryNote);
  }, [activeTab, activeTabAllowed, inventoryView, businessUnitId, fetchCurrentStock, crossTabRefreshToken]);

  const stockRows = useMemo(() => stock
    .filter((balance) => !isDirectConsumptionItem(balance.item || balance))
    .map((balance, index) => buildRow(balance, index, languageId)), [stock, languageId]);
  const hasRestrictedStockLocationAccess = stockRows.some(
    (row) => row.locationAccessScope === "user_locations"
  );

  const allLocationColumns = useMemo(() => {
    const byId = new Map();

    stockLocations.forEach((location) => {
      const id = Number(location.id);
      if (!Number.isFinite(id)) return;
      byId.set(id, {
        id,
        name: location.name || location.code || t("Location {0}", { 0: id }),
        code: location.code || "",
        is_main: Boolean(location.is_main),
        sort_order: Number(location.sort_order || 0),
      });
    });

    stockRows.forEach((row) => {
      row.locationBalances.forEach((locationBalance) => {
        const id = Number(locationBalance.location_id);
        if (!Number.isFinite(id) || byId.has(id)) return;
        const location = locationBalance.location || {};
        byId.set(id, {
          id,
          name: location.name || location.code || t("Location {0}", { 0: id }),
          code: location.code || "",
          is_main: Boolean(location.is_main),
          sort_order: Number(location.sort_order || 0),
        });
      });
    });

    return sortLocations(Array.from(byId.values()));
  }, [stockLocations, stockRows, t]);

  const visibleStockFilters = useMemo(
    () =>
      currentStockMode === "locations"
        ? STOCK_FILTERS.filter((filter) => filter.id !== "stale")
        : STOCK_FILTERS,
    [currentStockMode]
  );

  useEffect(() => {
    if (isMobile || (currentStockMode === "locations" && stockFilter === "stale")) {
      setStockFilter("all");
    }
  }, [currentStockMode, isMobile, stockFilter]);

  const filteredStock = useMemo(() => {
    const query = stockSearch.trim().toLowerCase();

    return stockRows.filter((row) => {
      if (query && !row.searchBlob.includes(query)) {
        return false;
      }

      if (stockFilter === "inStock") return row.quantity > 0;
      if (stockFilter === "outOfStock") return row.quantity <= 0;
      if (stockFilter === "stale") return row.status.key === "stale";
      return true;
    });
  }, [stockFilter, stockRows, stockSearch]);

  const locationColumns = useMemo(() => {
    const occupiedLocationIds = new Set();
    filteredStock.forEach((row) => {
      row.locationQtyById.forEach((quantity, locationId) => {
        if (hasStockQuantity(quantity) || (row.locationExactById.has(locationId) && row.locationExactById.get(locationId).steps !== "0")) occupiedLocationIds.add(locationId);
      });
    });
    return allLocationColumns.filter((location) => occupiedLocationIds.has(location.id));
  }, [allLocationColumns, filteredStock]);
  const showLegacyUnallocated = !hasRestrictedStockLocationAccess && filteredStock.some(
    (row) => hasStockQuantity(row.legacyUnallocatedQty)
  );

  const currentStockStats = useMemo(() => {
    const totalValue = filteredStock.reduce((sum, row) => sum + row.totalValue, 0);
    const outOfStock = filteredStock.filter((row) => row.quantity <= 0).length;
    const staleCount = filteredStock.filter((row) => row.status.key === "stale").length;

    return { totalValue, outOfStock, staleCount };
  }, [filteredStock]);

  const inventoryWorkspaceDescription = t(
    currentStockMode === "locations"
      ? hasRestrictedStockLocationAccess
        ? "Only your authorized locations and totals are shown."
        : "Compare quantities in locations with stock."
      : "Stock levels and inventory value. Open an item to trace its movements.");

  const pageCount = Math.max(1, Math.ceil(filteredStock.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedStock = filteredStock.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((row) => {
    const displayUnit = row.unitOptions.find((option) => stockUnitIdentity(option) === stockDisplayUnits[row.itemId])
      || row.unitOptions.find((option) => option.level === "unit")
      || { level: "unit", label: row.unitName, factor: 1 };
    const unresolved = row.physicalBasis?.level === displayUnit.level && !row.exactQuantity;
    const displayQuantity = unresolved ? null : quantityFromExact(row.exactQuantity, displayUnit.level, 4) ?? row.quantity * displayUnit.factor;
    return { ...row, displayUnit, displayQuantity, displayDigits: row.exactQuantity ? 4 : stockFractionDigits(displayQuantity) };
  });
  useEffect(() => setPage(1), [stockSearch, stockFilter, currentStockMode, businessUnitId, pageSize]);

  if (forcedTab && !tabPermissions[forcedTab]) {
    return <div className="pilcore-purchasing-ui inventory-intelligence sm-shell" data-theme={themeId}><div className="sm-empty" role="alert">{t("You don't have permission to access this section.")}</div></div>;
  }

  if (!activeTab || !activeTabAllowed) {
    return <div className="pilcore-purchasing-ui inventory-intelligence sm-shell" data-theme={themeId}><div className="sm-empty" role="alert">{t("You don't have permission to access this section.")}</div></div>;
  }

  if (isCurrentStockTab && (!businessUnitId || (inventoryView === "current" && loadedScope !== businessUnitId))) {
    return (
      <div className={`sm-shell ${useCompactWorkspaceShell ? "sm-shell--currentStock" : ""} ${isCurrentStockTab ? "pilcore-purchasing-ui inventory-intelligence" : ""}`.trim()} data-theme={isCurrentStockTab ? themeId : undefined}>
        <style>{isCurrentStockTab ? "" : STOCK_MANAGEMENT_STYLES}</style>
        <div className="sm-card sm-empty" role="status">{t(businessUnitId ? "Loading inventory..." : "Select a business unit to view inventory.")}</div>
      </div>
    );
  }

  if (isCurrentStockTab && inventoryView === "snapshot" && isSidebarMode) {
    return (
      <div className="pilcore-purchasing-ui inventory-intelligence sm-shell" data-theme={themeId}>
        <div className="sm-page sm-page--currentStock">
          <InventorySnapshotReportTab businessUnitId={businessUnitId} />
        </div>
      </div>
    );
  }

  return (
    <div className={`sm-shell ${useCompactWorkspaceShell ? "sm-shell--currentStock" : ""} ${isPilcoreStockWorkspace ? "pilcore-purchasing-ui" : ""} ${isStockOperationsWorkspace ? "stock-operations-workspace" : isPilcoreStockWorkspace ? "inventory-intelligence" : ""} ${isInventoryCountTab ? "sm-shell--counts" : ""}`.trim()} data-theme={isPilcoreStockWorkspace ? themeId : undefined}>
      <style>{isPilcoreStockWorkspace ? "" : STOCK_MANAGEMENT_STYLES}</style>

      {isCurrentStockTab && inventoryView === "current" && (stockError || locationsError) && <div className="sm-error" role="alert">
        <span>{t(stockError ? "Could not load inventory. Last available data is shown, if any." : "Locations could not be loaded. The location view may be incomplete.")}</span>
        <button type="button" className="sm-actionBtn" onClick={fetchCurrentStock} disabled={loading}>{t("Retry")}</button>
      </div>}

      <div className={`sm-page ${useCompactWorkspaceShell ? "sm-page--currentStock" : ""}`.trim()}>
        {showHero ? (
          <section className="sm-hero">
            <div>
              <div className="sm-kicker">{t("Inventory Workspace")}</div>
              <h1 className="sm-title">{isSidebarMode ? TAB_TITLES[activeTab] || "Stock Management" : t("Stock Management")}</h1>
              <p className="sm-subtitle">
                Monitor current on-hand stock with the same Pilcore language as the rest of Purchasing. Search fast, spot zero stock,
                and review value concentration without digging through legacy tables.
              </p>
            </div>

            <div className="sm-badge">{TAB_TITLES[activeTab] || "Stock Management"}</div>
          </section>
        ) : null}

        {!isSidebarMode ? (
          <div className="sm-tabs">
            {Object.entries(TAB_TITLES).map(([tabId, tabLabel]) => {
              if (tabId === "stockMovements" || tabId === "snapshotReport") return null;
              const isVisible =
                tabId === "stockRequisitions"
                  ? tabPermissions.stockRequisitions || tabPermissions.stockMovements
                  : tabPermissions[tabId];
              const targetTab =
                tabId === "stockRequisitions"
                  ? tabPermissions.stockRequisitions
                    ? "stockRequisitions"
                    : "stockMovements"
                  : tabId;

              return isVisible ? (
                <button
                  key={tabId}
                  type="button"
                  className={`sm-tab ${mainActiveTab === tabId ? "sm-tab--active" : ""}`}
                  onClick={() => setActiveTab(targetTab)}
                >
                  {t(tabLabel)}
                </button>
              ) : null;
            })}
          </div>
        ) : null}

        {activeTab === "currentStock" && (
          <section className="sm-card sm-card--currentStock" aria-busy={inventoryView === "current" && loading}>
            <div className="sm-cardHead sm-cardHead--currentStock">
              <div className="sm-controls">
                <div className="sm-viewTabs">
                  {canViewCurrentStock && <button
                    type="button"
                    className={`sm-viewTab ${inventoryView === "current" ? "sm-viewTab--active" : ""}`}
                    aria-pressed={inventoryView === "current"}
                    onClick={() => setInventoryView("current")}
                  >{t("Current Stock")}</button>}
                  {canViewCurrentStock && <button
                    type="button"
                    className={`sm-viewTab ${inventoryView === "directConsumption" ? "sm-viewTab--active" : ""}`}
                    aria-pressed={inventoryView === "directConsumption"}
                    onClick={() => setInventoryView("directConsumption")}
                  >{t("Direct Consumption")}</button>}
                  {!isSidebarMode && canViewSnapshot && <button
                    type="button"
                    className={`sm-viewTab ${inventoryView === "snapshot" ? "sm-viewTab--active" : ""}`}
                    aria-pressed={inventoryView === "snapshot"}
                    onClick={() => setInventoryView("snapshot")}
                  >{t("Snapshot Ledger")}</button>}
                </div>

                {inventoryView === "current" ? (
                  <>
                    <span className="sm-workspaceLabel" title={inventoryWorkspaceDescription}>{t("Current Stock")}</span>
                    <input
                      type="search"
                      className="sm-search" aria-label={t("Search by family, subfamily, category, code, item or unit...")}
                      value={stockSearch}
                      onChange={(event) => setStockSearch(event.target.value)}
                      placeholder={t("Search by family, subfamily, category, code, item or unit...")}
                    />

                    <div className="sm-filterRow sm-filterRow--mode" aria-label={t("Current stock view mode")}>
                      <button
                        type="button"
                        className={`sm-filter ${currentStockMode === "summary" ? "sm-filter--active" : ""}`}
                        aria-pressed={currentStockMode === "summary"}
                        onClick={() => setCurrentStockMode("summary")}
                      >{t("Summary")}</button>
                      <button
                        type="button"
                        className={`sm-filter ${currentStockMode === "locations" ? "sm-filter--active" : ""}`}
                        aria-pressed={currentStockMode === "locations"}
                        onClick={() => setCurrentStockMode("locations")}
                      >{t("By Location")}</button>
                    </div>

                    {!isMobile && <div className="sm-filterRow">
                      {visibleStockFilters.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          className={`sm-filter ${stockFilter === filter.id ? "sm-filter--active" : ""}`}
                          aria-pressed={stockFilter === filter.id}
                          onClick={() => setStockFilter(filter.id)}
                        >
                          {t(filter.label)}
                        </button>
                      ))}
                    </div>}
                  </>
                ) : null}
              </div>
            </div>

            {inventoryView === "current" ? (
              <>
                <div className="sm-stats">
                  <div className="sm-stat">
                    <div className="sm-statLabel">{t("Visible Items")}</div>
                    <div className="sm-statValue">{filteredStock.length}</div>
                  </div>
                  <div className="sm-stat">
                    <div className="sm-statLabel">{t("Inventory Value")}</div>
                    <div className="sm-statValue">{formatCurrency(currentStockStats.totalValue)}</div>
                  </div>
                  <div className="sm-stat">
                    <div className="sm-statLabel">{t("Out Of Stock")}</div>
                    <div className="sm-statValue">{currentStockStats.outOfStock}</div>
                  </div>
                  <div className="sm-stat">
                    <div className="sm-statLabel">{currentStockMode === "locations" ? t("Locations") : t("Stale Lines")}</div>
                    <div className="sm-statValue">{currentStockMode === "locations" ? locationColumns.length : currentStockStats.staleCount}</div>
                  </div>
                </div>

                <div className="sm-cardBody">
                  {!filteredStock.length ? (
                    <div className="sm-empty">{t("No inventory lines match the current search or filters.")}</div>
                  ) : isMobile ? (
                    <div className="sm-mobileList">
                      {pagedStock.map((row) =>
                        currentStockMode === "locations" ? (
                          <article key={row.id} className="sm-mobileCard">
                            <div className="sm-mobileTop">
                              <div>
                                <div className="sm-code">{row.itemCode}</div>
                                <div className="sm-itemName" title={row.itemName}>
                                  <button type="button" className="sm-itemButton" onClick={() => openMovementLedger(row)} disabled={!row.itemId || !canViewMovements}>{row.itemName}</button>
                                </div>
                              </div>
                              <span className="sm-unitBadge" title={t("Inventory unit: {0}", { 0: row.unitName })}>
                                {row.unitName}
                              </span>
                            </div>
                            <div className="sm-hierarchy">
                              {row.familyName} / {row.subfamilyName} / {row.categoryName} / {row.subcategoryName}
                            </div>

                            <div className="sm-mobileLocationList">
                              {locationColumns.filter((location) => row.locationExactById.has(location.id) ? row.locationExactById.get(location.id).steps !== "0" : hasStockQuantity(row.locationQtyById.get(location.id))).map((location) => {
                                const qty = Number(row.locationQtyById.get(Number(location.id)) || 0);

                                return (
                                  <button
                                    key={location.id}
                                    type="button"
                                    className="sm-mobileLocationRow sm-mobileLocationButton"
                                    aria-label={t("View {0} movements for {1}", { 0: row.itemName, 1: location.name })}
                                    onClick={() =>
                                      openMovementLedger(row, `location:${location.id}`, {
                                        scope_key: `location:${location.id}`,
                                        name: location.name,
                                        current_qty: qty,
                                      })
                                    }
                                    disabled={!row.itemId || !canViewMovements}
                                  >
                                    <span>{location.name}</span>
                                    <strong className={qty === 0 ? "sm-locationQty--empty" : "sm-locationQty"}>
                                      {renderLocationQuantity(qty, row, true, row.locationExactById.get(Number(location.id)))}
                                    </strong>
                                  </button>
                                );
                              })}
                              {row.locationAccessScope !== "user_locations" && hasStockQuantity(row.legacyUnallocatedQty) ? (
                                <button
                                  type="button"
                                  className="sm-mobileLocationRow sm-mobileLocationButton"
                                  aria-label={t("View {0} Legacy / Unallocated movements", { 0: row.itemName })}
                                  onClick={() =>
                                    openMovementLedger(row, "legacy_unallocated", {
                                      scope_key: "legacy_unallocated",
                                      name: "Legacy / Unallocated",
                                      current_qty: row.legacyUnallocatedQty,
                                    })
                                  }
                                  disabled={!row.itemId || !canViewMovements}
                                >
                                  <span>{t("Legacy / Unallocated")}</span>
                                  <strong
                                    className={
                                      Number(row.legacyUnallocatedQty || 0) === 0
                                        ? "sm-locationQty--empty"
                                        : "sm-locationQty"
                                    }
                                  >
                                    {renderLocationQuantity(row.legacyUnallocatedQty, row)}
                                  </strong>
                                </button>
                              ) : null}
                              <div className="sm-mobileLocationRow">
                                <span>{t("Total locations")}</span>
                                <strong className="sm-locationTotal">
                                  {renderLocationQuantity(row.locationTotalQty, row)}
                                </strong>
                              </div>
                              <button
                                type="button"
                                className="sm-mobileLocationRow sm-mobileLocationButton"
                                aria-label={t("View {0} {1} movements", { 0: t(row.locationAccessScope === "user_locations" ? "authorized" : "all"), 1: row.itemName })}
                                onClick={() => openMovementLedger(row, "global")}
                                disabled={!row.itemId || !canViewMovements}
                              >
                                <span>
                                  {row.locationAccessScope === "user_locations" ? t("Authorized total") : t("Global total")}
                                </span>
                                <strong>
                                  {renderLocationQuantity(row.quantity, row, true, row.storedExact)}
                                </strong>
                              </button>
                            </div>
                          </article>
                        ) : (
                          <article key={row.id} className="sm-mobileCard">
                            <div className="sm-mobileTop">
                              <div>
                                <div className="sm-code">{row.itemCode}</div>
                                <div className="sm-itemName" title={row.itemName}>
                                  <button type="button" className="sm-itemButton" onClick={() => openMovementLedger(row)} disabled={!row.itemId || !canViewMovements}>{row.itemName}</button>
                                </div>
                              </div>
                              <span className={`sm-pill ${row.status.toneClass}`}>{t(row.status.label)}</span>
                            </div>

                            <div className="sm-hierarchy">
                              {row.familyName} / {row.subfamilyName} / {row.categoryName} / {row.subcategoryName}
                            </div>

                            <div className="sm-mobileRows">
                              <div>
                                <div className="sm-mobileLabel">{t("Quantity")}</div>
                                <div className="sm-mobileValue">
                                  <InventoryQuantity
                                    value={row.displayQuantity}
                                    optional
                                    formatted={formatExactQuantity(row.storedExact, row.displayUnit.level, languageId)}
                                    unavailableTitle={row.displayQuantity == null ? t("Exact subunit balance unavailable") : undefined}
                                    maximumFractionDigits={row.displayDigits}
                                    unitName={row.displayUnit.label}
                                    itemName={row.itemName}
                                    unitOptions={row.unitOptions}
                                    selectedLevel={row.displayUnit.level}
                                    onUnitChange={(option) => selectStockDisplayUnit(row.itemId, option)}
                                  />
                                </div>
                              </div>
                              <div>
                                <div className="sm-mobileLabel">{t("Stock Value")}</div>
                                <div className="sm-mobileValue">{formatCurrency(row.totalValue)}</div>
                              </div>
                              <div>
                                <div className="sm-mobileLabel">{t("Last Movement")}</div>
                                <div className="sm-mobileValue">{formatDate(row.lastMovementAt)}</div>
                              </div>
                            </div>

                            <div className="sm-mobileActions">
                              <button
                                type="button"
                                className="sm-actionBtn"
                                onClick={() => openMovementLedger(row)}
                                disabled={!row.itemId || !canViewMovements}
                              >{t("Movements")}</button>
                            </div>
                          </article>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="sm-tableWrap">
                      {currentStockMode === "locations" ? (
                        <table
                          className="sm-table sm-table--currentStock sm-table--locations"
                          style={{
                            minWidth: `${
                              1040 +
                              locationColumns.length * 132 +
                              (showLegacyUnallocated ? 150 : 0)
                            }px`,
                          }}
                        >
                          <thead>
                            <tr>
                                <th>{t("Code")}</th>
                                <th>{t("Item")}</th>
                                <th>{t("Unit")}</th>
                                <th className="sm-num">
                                {hasRestrictedStockLocationAccess ? t("Authorized Total") : t("Global Total")}
                              </th>
                                {locationColumns.map((location) => (
                                <th key={location.id} className="sm-num" title={location.code || location.name}>
                                  {location.name}
                                </th>
                              ))}
                                {showLegacyUnallocated ? (
                                <th className="sm-num">{t("Legacy / Unallocated")}</th>
                              ) : null}
                                <th className="sm-num">{t("Locations Total")}</th>
                                <th>{t("Family")}</th>
                                <th>{t("Subfamily")}</th>
                                <th>{t("Category")}</th>
                                <th>{t("Subcategory")}</th>
                              </tr>
                          </thead>
                          <tbody>
                            {pagedStock.map((row) => (
                              <tr key={row.id}>
                                <td title={row.itemCode}>
                                  <span className="sm-code">{row.itemCode}</span>
                                </td>
                                <td className="sm-cellStrong" title={row.itemName}>
                                  <button type="button" className="sm-itemButton" onClick={() => openMovementLedger(row)} disabled={!row.itemId || !canViewMovements}>{row.itemName}</button>
                                </td>
                                <td>
                                  <span className="sm-unitBadge" title={t("Inventory unit: {0}", { 0: row.unitName })}>
                                    {row.unitName}
                                  </span>
                                </td>
                                <td className="sm-num">
                                  <button
                                    type="button"
                                    className="sm-locationMovementBtn"
                                    aria-label={t("View {0} {1} movements", { 0: t(row.locationAccessScope === "user_locations" ? "authorized" : "all"), 1: row.itemName })}
                                    onClick={() => openMovementLedger(row, "global")}
                                    disabled={!row.itemId || !canViewMovements}
                                  >
                                    {renderLocationQuantity(row.quantity, row, true, row.storedExact)}
                                  </button>
                                </td>
                                {locationColumns.map((location) => {
                                  const qty = Number(row.locationQtyById.get(Number(location.id)) || 0);

                                  return (
                                    <td
                                      key={location.id}
                                      className={`sm-num ${qty === 0 ? "sm-locationQty--empty" : "sm-locationQty"}`}
                                    >
                                      <button
                                        type="button"
                                        className="sm-locationMovementBtn"
                                        aria-label={t("View {0} movements for {1}", { 0: row.itemName, 1: location.name })}
                                        onClick={() =>
                                          openMovementLedger(row, `location:${location.id}`, {
                                            scope_key: `location:${location.id}`,
                                            name: location.name,
                                            current_qty: qty,
                                          })
                                        }
                                        disabled={!row.itemId || !canViewMovements}
                                      >
                                        {renderLocationQuantity(qty, row, false, row.locationExactById.get(Number(location.id)))}
                                      </button>
                                    </td>
                                  );
                                })}
                                {showLegacyUnallocated ? (
                                  <td
                                    className={`sm-num ${
                                      Number(row.legacyUnallocatedQty || 0) === 0
                                        ? "sm-locationQty--empty"
                                        : "sm-locationQty"
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      className="sm-locationMovementBtn"
                                      aria-label={t("View {0} Legacy / Unallocated movements", { 0: row.itemName })}
                                      onClick={() =>
                                        openMovementLedger(row, "legacy_unallocated", {
                                          scope_key: "legacy_unallocated",
                                          name: "Legacy / Unallocated",
                                          current_qty: row.legacyUnallocatedQty,
                                        })
                                      }
                                      disabled={!row.itemId || !canViewMovements}
                                    >
                                      {renderLocationQuantity(row.legacyUnallocatedQty, row, false)}
                                    </button>
                                  </td>
                                ) : null}
                                <td className="sm-num sm-locationTotal">{renderLocationQuantity(row.locationTotalQty, row, false)}</td>
                                <td className="sm-cellMuted" title={row.familyName}>{row.familyName}</td>
                                <td className="sm-cellMuted" title={row.subfamilyName}>{row.subfamilyName}</td>
                                <td className="sm-cellMuted" title={row.categoryName}>{row.categoryName}</td>
                                <td className="sm-cellMuted" title={row.subcategoryName}>{row.subcategoryName}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="sm-table sm-table--currentStock" style={{ "--stock-fraction-width": pagedStock.some((row) => row.displayDigits === 8) ? "8ch" : "2ch" }}>
                          <thead>
                            <tr>
                                <th>{t("Code")}</th>
                                <th>{t("Item")}</th>
                                <th className="sm-num" style={{ textAlign: "center" }}>{t("Quantity")}</th>
                                <th>{t("Status")}</th>
                                <th className="sm-num">{t("Stock Value")}</th>
                                <th>{t("Last Movement")}</th>
                                <th>{t("Family")}</th>
                                <th>{t("Subfamily")}</th>
                                <th>{t("Category")}</th>
                                <th>{t("Subcategory")}</th>
                                <th>{t("Movements")}</th>
                              </tr>
                          </thead>
                          <tbody>
                            {pagedStock.map((row) => (
                              <tr key={row.id}>
                                <td title={row.itemCode}>
                                  <span className="sm-code">{row.itemCode}</span>
                                </td>
                                <td className="sm-cellStrong" title={row.itemName}>
                                  <button type="button" className="sm-itemButton" onClick={() => openMovementLedger(row)} disabled={!row.itemId || !canViewMovements}>{row.itemName}</button>
                                </td>
                                <td className="sm-num">
                                  <InventoryQuantity
                                    alignDecimals
                                    value={row.displayQuantity}
                                    optional
                                    formatted={formatExactQuantity(row.storedExact, row.displayUnit.level, languageId)}
                                    unavailableTitle={row.displayQuantity == null ? t("Exact subunit balance unavailable") : undefined}
                                    maximumFractionDigits={row.displayDigits}
                                    unitName={row.displayUnit.label}
                                    itemName={row.itemName}
                                    unitOptions={row.unitOptions}
                                    selectedLevel={row.displayUnit.level}
                                    onUnitChange={(option) => selectStockDisplayUnit(row.itemId, option)}
                                  />
                                </td>
                                <td>
                                  <span className={`sm-pill ${row.status.toneClass}`}>{t(row.status.label)}</span>
                                </td>
                                <td className="sm-num">{formatCurrency(row.totalValue)}</td>
                                <td className="sm-cellMuted">{formatDate(row.lastMovementAt)}</td>
                                <td className="sm-cellMuted" title={row.familyName}>{row.familyName}</td>
                                <td className="sm-cellMuted" title={row.subfamilyName}>{row.subfamilyName}</td>
                                <td className="sm-cellMuted" title={row.categoryName}>{row.categoryName}</td>
                                <td className="sm-cellMuted" title={row.subcategoryName}>{row.subcategoryName}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="sm-actionBtn"
                                    onClick={() => openMovementLedger(row)}
                                    disabled={!row.itemId || !canViewMovements}
                                  >{t("View")}</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : inventoryView === "directConsumption" ? (
              <DirectConsumptionPurchases key={businessUnitId} businessUnitId={businessUnitId} canView={canViewCurrentStock} refreshToken={crossTabRefreshToken} />
            ) : (
              <InventorySnapshotReportTab businessUnitId={businessUnitId} />
            )}
            {inventoryView === "current" && <div className="sm-pagination">
              <span aria-live="polite">{t("{0}–{1} of {2} items", { 0: filteredStock.length ? (currentPage - 1) * pageSize + 1 : 0,
                1: Math.min(currentPage * pageSize, filteredStock.length), 2: filteredStock.length })}</span>
              <div className="sm-paginationControls">
                {(stockSearch || stockFilter !== "all") && <button type="button" onClick={() => { setStockSearch(""); setStockFilter("all"); }}>{t("Clear filters")}</button>}
                <label htmlFor="stock-page-size">{t("Rows per page")}</label>
                <select id="stock-page-size" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                  {[50,100,200].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
                <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>{t("Previous")}</button>
                <span>{t("Page {0} of {1}", { 0: currentPage, 1: pageCount })}</span>
                <button type="button" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>{t("Next")}</button>
              </div>
            </div>}
          </section>
        )}

        {isStockOperationsTab && (
          <StockOperationsWorkspace
            businessUnitId={businessUnitId}
            initialStatusFilter="all"
            initialView={pendingApprovalsOnly ? "pendingApprovals" : activeTab === "stockMovements" ? "dispatchHistory" : "requests"}
            canViewRequisitions={canViewStockRequisitionFlow}
            canViewDispatches={canViewStockDispatchFlow}
          />
        )}
        {activeTab === "stockReturns" && <StockReturnsWorkspace businessUnitId={businessUnitId} />}
        {activeTab === "stockTransfers" && <StockTransfersWorkspace businessUnitId={businessUnitId} pendingApprovalsOnly={pendingApprovalsOnly} />}
        {activeTab === "inventoryCount" && <InventoryCountTab businessUnitId={businessUnitId} />}
      </div>

      {movementModalRow && canViewMovements ? (
        <ItemMovementHistory
          businessUnitId={businessUnitId}
          row={movementModalRow}
          onClose={closeMovementLedger}
        />
      ) : null}
    </div>
  );
}
